import { useEffect, useRef, useState } from 'react';
import type { AbnLookupResult } from '@l2u/shared/schemas';

type ApiPost = <T>(path: string, body?: unknown) => Promise<T>;

export type AbnLookupState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; result: AbnLookupResult };

// Debounced (500ms) ABN lookup. Fires only once the value is a complete
// 11-digit ABN (spaces stripped); stale responses are ignored via a request id.
export function useAbnLookup(abn: string, post: ApiPost, path: string): AbnLookupState {
  const [state, setState] = useState<AbnLookupState>({ status: 'idle' });
  const reqId = useRef(0);
  useEffect(() => {
    const cleaned = abn.replace(/\s+/g, '');
    if (!/^\d{11}$/.test(cleaned)) {
      setState({ status: 'idle' });
      return;
    }
    const id = ++reqId.current;
    setState({ status: 'loading' });
    const timer = setTimeout(() => {
      post<{ result: AbnLookupResult }>(path, { abn: cleaned })
        .then((r) => { if (id === reqId.current) setState({ status: 'done', result: r.result }); })
        .catch(() => { if (id === reqId.current) setState({ status: 'done', result: { ok: false, reason: 'lookup_failed' } }); });
    }, 500);
    return () => clearTimeout(timer);
  }, [abn, post, path]);
  return state;
}
